// MA-C13 (ADR-013 follow-up) — `discrete_clusters` concept visual for equal-groups.
//
// Shows `a` DISTINCT CLUSTERS of `b` dots each — making "N groups of M" visible.
// Total dot count = a×b = expectedAnswer. No operator symbol shown.
//
// ANSWER-SAFE by construction:
//   • Clusters are visually enclosed (ellipse outline) with a clear inter-cluster gap.
//   • Each cluster contains exactly `b` dots — the child sees the grouping structure.
//   • No numeral labels or "×" / "+" operators — the child counts and multiplies.
//   • The aria-label describes the grouping ("N groups of M"), never the total count.
//
// ADDITIVE — registers itself under the "discrete_clusters" kind (see bottom of
// file). The mapper (contentToEngineItem.ts) emits this kind for `equal_groups`
// items only. No edit to MathVisual or the session screens needed.
//
// PROPS (MathRendererProps contract):
//   a     — number of clusters (groups); clamped to MAX_CLUSTERS
//   b     — dots per cluster (per-group count); clamped to MAX_PER
//   total — a*b; validated at render (clamped dots use safeA*safeB, not total)
//   op    — unused
import type { MathRendererProps } from "./rendererRegistry";

const DOT_FILL    = "var(--pal-orange, #d97742)";
const RING_STROKE = "var(--color-border-secondary, #cdbfae)";

// MA-659 (MA-651 batch 3, RENDER-D; corrected round 2, MF-4): refreshed —
// the pool is now 36 launch_safe equal_groups items (ms.math.17), not 18.
// Five items sit exactly on the MAX_CLUSTERS clamp (a=6:
// MEG_M17_004/005/006/007/008); one item, ME_M17_016 (a=2, b=7), sits one
// below MAX_PER — the largest per-cluster count in the pool is b=7, not b=6
// as an earlier pass here wrongly claimed, leaving only a 1-item margin to
// MAX_PER=8, not "well inside" it. Emission is gated mapper-side on
// `groups <= 6 && per <= 8` (contentToEngineItem.ts) so a future item that
// would exceed either clamp is suppressed rather than silently drawing the
// wrong number of groups/dots.
const MAX_CLUSTERS = 6;
const MAX_PER      = 8;

const DOT_R       = 10;  // dot radius (matches DivisionSharingRenderer pile)
const DOT_PITCH   = 26;  // dot center-to-center inside a cluster
const INNER_COLS  = 2;   // dot columns per row inside each cluster
const CLUSTER_PAD = 10;  // gap from outermost dot center to ellipse edge
const CLUSTER_GAP = 16;  // gap between adjacent cluster ellipses (edge-to-edge)
const SVG_MARGIN  = 8;   // gap from SVG edge to cluster ellipse edge

// Layout: up to 3 clusters per row (matches DivisionSharingRenderer ring cadence)
const CLUSTER_LAYOUT_COLS = 3;

export default function DiscreteClustersRenderer({ a, b }: MathRendererProps) {
  const safeA = Math.max(1, Math.min(MAX_CLUSTERS, Math.round(Number.isFinite(a ?? NaN) ? (a as number) : 2)));
  const safeB = Math.max(1, Math.min(MAX_PER,      Math.round(Number.isFinite(b ?? NaN) ? (b as number) : 2)));

  // ── Cluster content geometry ─────────────────────────────────────────────
  const innerCols = Math.min(safeB, INNER_COLS);  // 1 (b=1) or 2 (b≥2)
  const innerRows = Math.ceil(safeB / INNER_COLS); // number of dot rows per cluster

  // Half-span from cluster center to outermost dot center
  const halfGridW = (innerCols - 1) * DOT_PITCH / 2;  // 0 (b=1) or 13 (b≥2)
  const halfGridH = (innerRows - 1) * DOT_PITCH / 2;   // 0 for b≤2; 13 for b≤4; etc.

  // Ellipse radii: large enough to enclose all dots with CLUSTER_PAD clearance
  const rx = Math.max(halfGridW + DOT_R + CLUSTER_PAD, 26);
  const ry = Math.max(halfGridH + DOT_R + CLUSTER_PAD, 22);

  // ── Cluster layout (grid of ellipses) ───────────────────────────────────
  const pitchX   = rx * 2 + CLUSTER_GAP;  // center-to-center horizontal
  const pitchY   = ry * 2 + CLUSTER_GAP;  // center-to-center vertical
  const marginX  = rx + SVG_MARGIN;        // first cluster center x
  const marginY  = ry + SVG_MARGIN;        // first cluster center y

  const layoutRows = Math.ceil(safeA / CLUSTER_LAYOUT_COLS);
  const colsUsed   = Math.min(safeA, CLUSTER_LAYOUT_COLS);

  const svgWidth  = marginX * 2 + (colsUsed - 1) * pitchX;
  const svgHeight = marginY * 2 + (layoutRows - 1) * pitchY;

  // Cluster center coordinates
  const clusters = Array.from({ length: safeA }).map((_, i) => ({
    cx: marginX + (i % CLUSTER_LAYOUT_COLS) * pitchX,
    cy: marginY + Math.floor(i / CLUSTER_LAYOUT_COLS) * pitchY,
  }));

  // Dot coordinates for one cluster centered at (cx, cy).
  // When safeB is odd, the last dot is centered horizontally (lone dot in last row).
  function dotsForCluster(cx: number, cy: number): { x: number; y: number }[] {
    return Array.from({ length: safeB }).map((_, j) => {
      const isLoneDot = safeB % 2 === 1 && j === safeB - 1;
      const col = isLoneDot ? 0 : j % INNER_COLS;
      const row = Math.floor(j / INNER_COLS);
      const rowHalfW = isLoneDot ? 0 : halfGridW;
      return {
        x: cx - rowHalfW + col * DOT_PITCH,
        y: cy - halfGridH + row * DOT_PITCH,
      };
    });
  }

  return (
    <svg
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      width="100%"
      style={{ maxWidth: Math.min(300, svgWidth) }}
      role="img"
      aria-label={`${safeA} groups of ${safeB}`}
    >
      {clusters.map(({ cx, cy }, i) => (
        <g key={`cluster-${i}`}>
          {/* Cluster boundary — visually separates this group from its neighbours */}
          <ellipse
            cx={cx}
            cy={cy}
            rx={rx}
            ry={ry}
            fill="none"
            stroke={RING_STROKE}
            strokeWidth="2"
          />
          {/* Dots inside this cluster — each is a <circle> for the integrity counter */}
          {dotsForCluster(cx, cy).map((d, j) => (
            <circle key={`dot-${i}-${j}`} cx={d.x} cy={d.y} r={DOT_R} fill={DOT_FILL} />
          ))}
        </g>
      ))}
    </svg>
  );
}
